/**
 * Lane SCENE-PLAINS: the few kit pieces the outer grounds of the plains lacked (stepping stones over the stream,
 * grave slabs, a stockade palisade, a cracked temple bell, a log pile, a camp fire). Upright cards anchored at the foot,
 * built with the same helpers as props.ts / props2.ts; ideas only, our own designs. Collision data: the stepping
 * stones are walkable deck tiles (`plainsLayout.STEPPING_STONES`, plainsMap.ts); the others are `solid` where placed
 * (kit/outer.ts, plainsMap.ts for the shared ones).
 */
import { box, M, snap } from '../buildings';
import type { Sprite } from '../scenery';
import { blob, K, propPix } from './parts';
import { fin, pole, stoneT, wood } from './propBase';

/** A flat river stone, 1.2 m across, standing 0.3 m (≈ 5 px) over the water, with a wet dark foot and a lit top. */
function steppingStone(): Sprite {
  const p = propPix(44, 26, 22, 14);
  blob(p.px, p.ax, p.ay + 1, 15, 5, M.blue, 0.35); // ripple ring on the water
  blob(p.px, p.ax, p.ay - 1, 13, 5, M.stone, 0.3);
  blob(p.px, p.ax, p.ay - 4, 12, 4.5, M.stone, 0.62);
  p.px.rect(p.ax - 4, p.ay - 6, 5, 1, M.stone[4]!);
  p.px.rect(p.ax + 3, p.ay - 3, 3, 1, M.stone[1]!);
  return fin(p);
}

/** A grave slab: a headstone on a low earth mound with a few dried flowers (≈ 30 px). */
function tombstone(tall: boolean): Sprite {
  const h = tall ? 30 : 22;
  const p = propPix(34, 46, 17, 38);
  blob(p.px, p.ax, p.ay - 1, 11, 4, K.mud, 0.5);
  box(p.px, p.P, [-0.28, -0.08, 0.28, 0.08], 2, h, (u, v, l) => (Math.abs(u) < 2 && v > 8 && v < h - 4 && ((v | 0) % 4 < 2) ? M.stone[1]! : stoneT(u, v, l)));
  blob(p.px, p.ax, p.ay - h - 1, 5, 2.5, M.stone, 0.65);
  p.px.rect(p.ax + 7, p.ay - 4, 1, 3, K.straw[2]!);
  p.px.rect(p.ax + 9, p.ay - 5, 1, 4, M.red[3]!);
  return fin(p);
}

/** A palisade of sharpened logs, 2 tiles long along x or y, bound with rope, a dark mud foot (≈ 40 px). */
function palisade(along: 'x' | 'y'): Sprite {
  const p = propPix(80, 62, 40, 40);
  for (let k = 0; k <= 8; k++) {
    const t = -1 + k / 4;
    const [sx, sy] = along === 'x' ? p.P(t, 0, 0) : p.P(0, t, 0);
    const h = 30 + ((k * 5) % 3) * 3;
    for (let z = 0; z < h; z++) {
      const tip = h - z < 5 ? 5 - (h - z) : 0;
      for (let a = 0; a < 4 - Math.min(2, tip); a++) p.px.set(sx - 1 + a, sy - z, snap(M.wood, 0.28 + (a / 4) * 0.5 + (z % 7 === 0 ? -0.1 : 0), a, z, 0));
    }
  }
  for (const z of [9, 22]) {
    const [ax, ay] = along === 'x' ? p.P(-1, 0, z) : p.P(0, -1, z);
    const [bx, by] = along === 'x' ? p.P(1, 0, z) : p.P(0, 1, z);
    for (let k = 0; k <= 40; k++) p.px.set(ax + (bx - ax) * (k / 40), ay + (by - ay) * (k / 40), K.straw[1]!);
  }
  blob(p.px, p.ax, p.ay + 1, 17, 2.5, K.mud, 0.4);
  return fin(p);
}

/** The temple's cracked bell on a toppled frame: two posts, a crossbeam, a green bronze bell with a dark split (≈ 60 px). */
function brokenBell(): Sprite {
  const p = propPix(60, 78, 30, 70);
  pole(p.P, p, -0.7, 0, 0.1, 0, 52, M.lacquer);
  pole(p.P, p, 0.7, 0, 0.1, 0, 52, M.lacquer);
  box(p.px, p.P, [-0.85, -0.08, 0.85, 0.08], 50, 55, wood(0.05));
  const bx = p.ax;
  for (let z = 0; z < 24; z++) {
    const w = 6 + Math.round(Math.pow(z / 24, 2) * 5);
    for (let a = -w; a <= w; a++) {
      const crack = a === 1 + (z % 5 === 0 ? 1 : 0) && z < 18;
      p.px.set(bx + a, p.ay - 46 + z, crack ? K.greenTile[0]! : snap(K.greenTile, 0.4 + (a / (w + 1)) * 0.3 + (z < 4 ? 0.15 : 0), a, z, 0));
    }
  }
  p.px.rect(bx - 1, p.ay - 50, 3, 4, M.dark[2]!);
  return fin(p);
}

/** A stack of split logs and a chopping block with an axe (≈ 20 px). */
function logPile(): Sprite {
  const p = propPix(56, 40, 28, 30);
  for (let row = 0; row < 3; row++)
    for (let k = 0; k < 4 - row; k++) {
      const x = p.ax - 14 + row * 4 + k * 8;
      const y = p.ay - 4 - row * 7;
      blob(p.px, x, y, 4, 3.4, M.wood, 0.55);
      p.px.set(x, y, M.deck[3]!);
    }
  blob(p.px, p.ax + 20, p.ay - 1, 4, 3, M.wood, 0.5);
  p.px.rect(p.ax + 22, p.ay - 9, 1, 8, M.wood[1]!);
  p.px.rect(p.ax + 20, p.ay - 10, 4, 2, M.stone[3]!);
  return fin(p);
}

/** A camp fire ringed with stones and a spit (≈ 24 px); lit in the night pass (kit/lights.ts). */
function campfire(): Sprite {
  const p = propPix(40, 40, 20, 30);
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * Math.PI * 2;
    blob(p.px, p.ax + Math.cos(a) * 9, p.ay - 2 + Math.sin(a) * 4, 2.5, 2, M.stone, 0.55);
  }
  p.px.rect(p.ax - 4, p.ay - 4, 8, 2, M.wood[1]!);
  for (let z = 0; z < 12; z++) {
    const w = Math.max(0, 4 - Math.floor(z / 3));
    for (let a = -w; a <= w; a++) p.px.set(p.ax + a, p.ay - 5 - z, z < 4 ? M.red[3]! : z < 8 ? M.gold[3]! : M.gold[4]!);
  }
  return fin(p);
}

export const MAKERS3 = {
  steppingStone,
  tombstone: () => tombstone(false),
  tombstoneTall: () => tombstone(true),
  palisadeX: () => palisade('x'),
  palisadeY: () => palisade('y'),
  brokenBell,
  logPile,
  campfire,
} satisfies Record<string, () => Sprite>;

export type PropId3 = keyof typeof MAKERS3;
